import { type FormEvent, useEffect, useState } from "react";
import {
  type SystemTemplateDTO,
  createSystemTemplate,
  deleteSystemTemplate,
  getSuperadminApi,
  listSystemTemplates,
} from "@praesto/core";
import {
  Toolbar,
  Card,
  Button,
  Modal,
  FormField,
  Input,
  Select,
  Textarea,
  EmptyState,
} from "@praesto/ui-kit";

type Category = "frontoffice" | "housekeeping" | "kitchen" | "haccp" | "maintenance" | "generic";

export function SystemTemplatesTab() {
  const [templates, setTemplates] = useState<ReadonlyArray<SystemTemplateDTO>>([]);
  const [showNew, setShowNew] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const api = getSuperadminApi();
      const tok = await api.getAuthToken();
      const r = await listSystemTemplates(api.apiBase, tok);
      if (!cancelled) setTemplates(r.templates);
    })().catch((e) => {
      if (!cancelled) setError(String(e));
    });
    return () => {
      cancelled = true;
    };
  }, [reload]);

  async function del(id: string) {
    if (!confirm("Eliminare il template?")) return;
    try {
      const api = getSuperadminApi();
      const tok = await api.getAuthToken();
      await deleteSystemTemplate(api.apiBase, id, tok);
      setReload((r) => r + 1);
    } catch (e) {
      setError(String(e));
    }
  }

  return (
    <div>
      <Toolbar
        title={`System Templates (${templates.length})`}
        actions={
          <Button size="sm" variant="primary" onClick={() => setShowNew(true)}>
            + Nuovo template
          </Button>
        }
      />
      {error && <p className="text-sm text-danger mb-3">{error}</p>}
      {templates.length === 0 ? (
        <EmptyState
          title="Nessun template"
          description="Crea il primo per i clienti."
          size="compact"
        />
      ) : (
        <div className="space-y-2">
          {templates.map((t) => (
            <Card key={t.id} padding="sm">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm text-fg-default truncate">{t.title.it}</div>
                  <div className="text-xs text-fg-muted truncate">
                    {t.category} · {t.contentMarkdown.length} char
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-danger"
                  onClick={() => void del(t.id)}
                >
                  Elimina
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
      {showNew && (
        <NewTemplateModal
          onClose={() => setShowNew(false)}
          onDone={() => {
            setShowNew(false);
            setReload((r) => r + 1);
          }}
        />
      )}
    </div>
  );
}

function NewTemplateModal(props: { onClose: () => void; onDone: () => void }) {
  const [category, setCategory] = useState<Category>("frontoffice");
  const [titleIt, setTitleIt] = useState("");
  const [titleEn, setTitleEn] = useState("");
  const [descIt, setDescIt] = useState("");
  const [descEn, setDescEn] = useState("");
  const [content, setContent] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const api = getSuperadminApi();
      const tok = await api.getAuthToken();
      await createSystemTemplate(
        api.apiBase,
        {
          category,
          title: { it: titleIt, en: titleEn },
          description: { it: descIt, en: descEn },
          contentMarkdown: content,
        },
        tok,
      );
      props.onDone();
    } catch (e2) {
      setError(String(e2));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open={true} onClose={props.onClose} title="Nuovo system template" size="md">
      <form onSubmit={submit} className="space-y-3">
        <FormField label="Categoria">
          <Select value={category} onChange={(e) => setCategory(e.target.value as Category)}>
            <option value="frontoffice">frontoffice</option>
            <option value="housekeeping">housekeeping</option>
            <option value="kitchen">kitchen</option>
            <option value="haccp">haccp</option>
            <option value="maintenance">maintenance</option>
            <option value="generic">generic</option>
          </Select>
        </FormField>
        <FormField label="Titolo IT">
          <Input value={titleIt} required onChange={(e) => setTitleIt(e.target.value)} />
        </FormField>
        <FormField label="Title EN">
          <Input value={titleEn} required onChange={(e) => setTitleEn(e.target.value)} />
        </FormField>
        <FormField label="Descrizione IT">
          <Input value={descIt} required onChange={(e) => setDescIt(e.target.value)} />
        </FormField>
        <FormField label="Description EN">
          <Input value={descEn} required onChange={(e) => setDescEn(e.target.value)} />
        </FormField>
        <FormField label="Markdown contenuto (min 20 char)">
          <Textarea
            rows={10}
            value={content}
            required
            onChange={(e) => setContent(e.target.value)}
          />
        </FormField>
        <div className="flex gap-2 justify-end">
          <Button type="button" size="sm" variant="ghost" onClick={props.onClose}>
            Annulla
          </Button>
          <Button type="submit" size="sm" variant="primary" disabled={busy}>
            {busy ? "…" : "Crea"}
          </Button>
        </div>
        {error && <p className="text-sm text-danger">{error}</p>}
      </form>
    </Modal>
  );
}
